import { classNames, hasText } from "utils/helpers"
import { serverPlayRemoveStudent, serverPlayStart } from "utils/server"
import ActionButton from "../components/action-button"
import ResponsiveContainer from "../components/responsive-container"
import ResponsiveContent from "../components/responsive-content"
import { useMessages } from "../language/language"
import "./lobby-frame.less"
import Card from "../components/card"
import ScreenPager from "../components/screen-pager"
import LobbyStudent from "../components/lobby-student"
import Circle from "../components/circle"
import { ErrorAlert } from "../components/alert"
import {
  applyScreenToState,
  playErrorReducer,
  serverRequestErrorReducer,
  studentRemovedReducer,
} from "play/reducers"
import { ClientStudent } from "../play-model"
import { CSSProperties, useEffect, useMemo, useState } from "react"
import { ResponsiveSize, usePlayResponsive } from "../play-responsive-context"
import FlexContainer from "play/components/flex-container"
import If from "utils/components/if"
import { usePlayState } from "play/hooks/use-play-state"
import playEnvironment from "play/play-environment"

type QrCodeSize = "Tiny" | "Small" | "Normal" | "Big"

export default function LobbyFrame() {
  const {
    connectionId,
    lessonName,
    lessonImage,
    className,
    gameCode,
    studentsInLobby,
    classId,
    serverError,
    teacherConnectionId,
    modifyState,
    theme,
  } = usePlayState()

  const [isLoading, setIsLoading] = useState(false)
  const hasClass = hasText(classId)
  // no game when it could not be created: nothing to join, so no QR code and no address
  const hasGame = hasText(gameCode)
  const msg = useMessages()
  const [qrCodeIsZoomed, setQrCodeIsZoomed] = useState(false)

  const playLink = `${location.protocol}//${location.host}/${msg.locale_code}/play?code=${gameCode}`
  const playLinkText = `${location.host}/${msg.locale_code}/play`

  const errorText = msg.serverErrorToString(serverError)

  const countStudentsInClass = studentsInLobby.length
  let countStudentsJoined = 0
  for (let s of studentsInLobby) {
    if (s.hasJoined) {
      countStudentsJoined++
    }
  }

  const responsive = usePlayResponsive()

  let qrCodeSize = useMemo<QrCodeSize>(() => {
    let size: QrCodeSize = "Tiny"
    if (responsive.size >= ResponsiveSize.md) size = "Small"
    return size
  }, [responsive.size])

  const qrCodeUrl = `/${msg.locale_code}/qr/join/${gameCode}?size=${qrCodeSize}`

  useEffect(() => {
    if (hasText(lessonName)) {
      document.title = msg.pageTitle(lessonName!)
    } else {
      document.title = msg.defaultPageTitle
    }
  }, [lessonName])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && qrCodeIsZoomed) {
        setQrCodeIsZoomed(false)
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [qrCodeIsZoomed])

  async function handleStart() {
    if (!connectionId) {
      return console.error("No connection id!")
    }
    if (countStudentsJoined === 0) {
      return //console.log("No students have joined yet!");
    }

    setIsLoading(true)

    try {
      const resp = await serverPlayStart(connectionId!)

      setIsLoading(false)
      modifyState((state) => {
        console.log("teacherStarted")
        state.internalPage = null
        state.screen = resp.screen
        state.studentsInLobby = resp.students
        state.countStartedStudents = resp.students.filter(
          (x) => x.hasJoined
        ).length
        state.timer.running = false
        state.reportId = resp.reportId

        applyScreenToState(state, resp.screen)

        state.nextTimer = resp
        state.loadingReason = "LoadingImages"
        state.internalPage = "Loading"
        playEnvironment.connectionId = state.connectionId
        playEnvironment.oldGameCode = state.gameCode
      })
    } catch (e) {
      setIsLoading(false)
      modifyState((state) => {
        serverRequestErrorReducer(state, e)
        playErrorReducer(state, "StartGame", e)
      })
    }
  }

  async function handleKickOut(student: ClientStudent) {
    if (teacherConnectionId === null)
      throw "cannot kick out, teacher connection id is null"

    try {
      const resp = await serverPlayRemoveStudent({
        studentConnectionId: student.connectionId,
        teacherConnectionId,
      })
      if (!!resp) {
        modifyState((state) => {
          studentRemovedReducer(state, resp)
        })
      }
    } catch (e) {
      modifyState((state) => {
        serverRequestErrorReducer(state, e)
        playErrorReducer(state, "RemoveStudent", e)
      })
    }
  }

  let lobbyBottomStyle: CSSProperties = {}
  if (theme) {
    lobbyBottomStyle = {
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url(${theme.backgroundUrl})`,
      backgroundSize: "cover",
    }
  }

  return (
    <div
      className={classNames("lobby-frame", {
        zoomed: qrCodeIsZoomed,
      })}
      onClick={(e) => {
        if (qrCodeIsZoomed) {
          e.preventDefault()
          setQrCodeIsZoomed(false)
        }
      }}
    >
      <If
        condition={qrCodeIsZoomed == false}
        yes={
          <>
            <ResponsiveContainer
              centerContents={true}
              className={hasClass ? "lobby-top pager" : "lobby-top counter"}
            >
              <ResponsiveContent className="lobby-frame-content">
                <Card className="lobby-lesson-info" type="light">
                  <div className="lesson-image-container">
                    <If
                      condition={!!lessonImage}
                      yes={<img className="lesson-image" src={lessonImage!} />}
                      no={<>&nbsp;</>}
                    />
                  </div>
                  <div className="lesson-name-container">
                    <p className="text label lesson">
                      {msg.lobbyPage_lessonLabel}
                    </p>
                    <p className="text header lesson">
                      {hasText(lessonName) ? lessonName : "..."}
                    </p>
                    <ActionButton
                      className="start"
                      onClick={handleStart}
                      noBorder
                      disabled={isLoading || countStudentsJoined === 0}
                    >
                      {msg.lobbyPage_start}
                    </ActionButton>
                  </div>
                  {hasGame && (
                    <img
                      className="qr-image"
                      alt={gameCode.toString()}
                      src={qrCodeUrl}
                      onClick={(e) => {
                        e.preventDefault()
                        setQrCodeIsZoomed(true)
                      }}
                    />
                  )}
                </Card>
                {hasGame && (
                  <Card
                    className="lobby-session-info"
                    type="widget"
                    borders="no-top"
                  >
                    <p className="text instructions">
                      {msg.lobbyPage_goToAddreessLabel}
                    </p>
                    <p className="text address">
                      <a href={playLink} target="_blank">
                        {playLinkText}
                      </a>
                    </p>
                    <p className="text instructions">
                      {msg.lobbyPage_enterCodeLabel}
                    </p>
                    <p className="text code">{gameCode}</p>
                  </Card>
                )}
                <ErrorAlert error={errorText} hasError={!!errorText} />
              </ResponsiveContent>
            </ResponsiveContainer>
            <ResponsiveContainer
              centerContents={true}
              className={
                hasClass ? "lobby-bottom pager" : "lobby-bottom counter"
              }
              style={lobbyBottomStyle}
            >
              <ResponsiveContent className="lobby-frame-content">
                {hasClass ? (
                  <div className="pager-container">
                    <ScreenPager
                      className="lobby-framer"
                      current={countStudentsJoined}
                      total={countStudentsInClass}
                      size="big"
                    />
                  </div>
                ) : (
                  <div className="count-container">
                    <Circle className="lobby-counter">
                      {countStudentsJoined}
                    </Circle>
                  </div>
                )}
                <p className="count-label">
                  {hasClass ? className : msg.lobbyPage_studentsLabel}
                </p>
                <div className="students-container">
                  {studentsInLobby.map((student) => (
                    <LobbyStudent
                      className="student"
                      active={student.hasJoined}
                      key={`${student.userId}_${student.fullName}_${student.connectionId}`}
                      kickable={!hasClass || (hasClass && student.hasJoined)}
                      onKickOut={() => handleKickOut(student)}
                    >
                      {student.fullName}
                    </LobbyStudent>
                  ))}
                </div>
              </ResponsiveContent>
            </ResponsiveContainer>
          </>
        }
        no={
          <>
            <button
              className="btn btn-primary btn-circle"
              onClick={() => {
                setQrCodeIsZoomed(false)
              }}
            >
              &times;
            </button>
            <FlexContainer centerHorizontally centerVertically vertical>
              <label>{msg.gameCode_label}</label>
              <p>
                <strong style={{ fontSize: "3rem" }}>{gameCode}</strong>
              </p>
            </FlexContainer>
            <div
              className={"qr-image-zoomed"}
              style={{
                backgroundImage: `url(/${msg.locale_code}/qr/join/${gameCode}?size=Big)`,
              }}
              onClick={(e) => {
                e.preventDefault()
                setQrCodeIsZoomed(false)
              }}
            />
          </>
        }
      />
    </div>
  )
}
